// Procedural SVG glyphs drawn per sound family. These live outside the
// catalog so the catalog stays declarative. Each shape is hand-tuned
// to read at ~36×18px on a card — small enough to feel like a status
// LED, big enough to convey character.

import type { GlyphKind } from './catalog';

type Props = {
  kind: GlyphKind;
  className?: string;
};

export function SoundGlyph({ kind, className }: Props) {
  return (
    <svg
      viewBox="0 0 72 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {renderGlyph(kind)}
    </svg>
  );
}

function renderGlyph(kind: GlyphKind) {
  switch (kind) {
    case 'kick':
      // Sharp transient + exponential decay — a kick drum envelope.
      return (
        <>
          <path d="M2 20 L10 4 L14 20" />
          <path d="M14 20 Q 30 20, 40 16 T 70 14" opacity={0.55} />
        </>
      );
    case 'snare':
      // Noise burst + short tail.
      return (
        <>
          <path d="M4 20 L6 8 L10 18 L12 6 L16 20 L20 10 L24 18" />
          <path d="M26 14 L40 14" strokeDasharray="1 2" opacity={0.6} />
          <path d="M44 16 Q 56 16, 70 15" opacity={0.4} />
        </>
      );
    case 'hat':
      // Dense high-freq dots.
      return (
        <>
          {Array.from({ length: 12 }).map((_, i) => (
            <circle
              key={i}
              cx={4 + i * 5.5}
              cy={i % 2 === 0 ? 10 : 14}
              r={0.8}
              fill="currentColor"
              stroke="none"
            />
          ))}
        </>
      );
    case 'clap':
      // Two stacked bursts.
      return (
        <>
          <path d="M4 14 L8 6 L12 18 L16 8 L20 14" />
          <path d="M28 14 L32 4 L36 20 L40 6 L44 14" />
          <path d="M48 14 L68 14" opacity={0.4} strokeDasharray="1 2" />
        </>
      );
    case 'tom':
      // Pitched descending blob.
      return (
        <>
          <path d="M2 14 Q 10 4, 18 14 T 34 14 T 50 14 T 68 16" />
          <path d="M2 14 L68 14" opacity={0.2} strokeDasharray="1 3" />
        </>
      );
    case 'cymbal':
      // Fanning spray lines.
      return (
        <>
          <path d="M4 20 L36 4" />
          <path d="M10 20 L36 4" opacity={0.7} />
          <path d="M18 20 L36 4" opacity={0.55} />
          <path d="M26 20 L36 4" opacity={0.4} />
          <path d="M36 4 L46 20" opacity={0.7} />
          <path d="M36 4 L54 20" opacity={0.55} />
          <path d="M36 4 L62 20" opacity={0.4} />
          <path d="M36 4 L70 20" opacity={0.25} />
        </>
      );
    case 'shaker':
      // Scatter of short ticks.
      return (
        <>
          {[3, 8, 14, 19, 25, 30, 36, 41, 47, 52, 58, 63, 68].map((x, i) => (
            <path
              key={i}
              d={`M${x} ${i % 3 === 0 ? 8 : i % 3 === 1 ? 12 : 16} L${x} ${i % 3 === 0 ? 16 : i % 3 === 1 ? 14 : 20}`}
            />
          ))}
        </>
      );
    case 'tabla':
      // Pitched thump + dry tick glyph.
      return (
        <>
          <circle cx={14} cy={12} r={6} opacity={0.8} />
          <circle cx={14} cy={12} r={2.5} fill="currentColor" stroke="none" opacity={0.7} />
          <path d="M26 14 L32 6 L36 20 L40 10" opacity={0.8} />
          <path d="M46 14 L58 14" opacity={0.5} strokeDasharray="1 2" />
          <circle cx={64} cy={14} r={1.6} fill="currentColor" stroke="none" opacity={0.6} />
        </>
      );
    case 'saw':
      // Classic sawtooth.
      return (
        <path d="M2 18 L14 6 L14 18 L26 6 L26 18 L38 6 L38 18 L50 6 L50 18 L62 6 L62 18 L70 10" />
      );
    case 'square':
      // Classic square pulse.
      return (
        <path d="M2 18 L8 18 L8 6 L20 6 L20 18 L32 18 L32 6 L44 6 L44 18 L56 18 L56 6 L68 6 L68 18 L70 18" />
      );
    case 'sine':
      // Smooth cycle.
      return (
        <path d="M2 12 Q 10 2, 18 12 T 34 12 T 50 12 T 66 12" />
      );
    case 'triangle':
      // Triangle wave.
      return (
        <path d="M2 18 L12 4 L22 18 L32 4 L42 18 L52 4 L62 18 L70 10" />
      );
    case 'supersaw':
      // Stacked detuned saws.
      return (
        <>
          <path d="M2 20 L14 8 L14 20 L26 8 L26 20 L38 8 L38 20 L50 8 L50 20 L62 8 L62 20 L70 12" opacity={0.9} />
          <path d="M4 20 L16 6 L16 20 L28 6 L28 20 L40 6 L40 20 L52 6 L52 20 L64 6 L64 20" opacity={0.5} />
          <path d="M0 20 L12 10 L12 20 L24 10 L24 20 L36 10 L36 20 L48 10 L48 20 L60 10 L60 20" opacity={0.3} />
        </>
      );
    case 'pulse':
      // Narrow pulse.
      return (
        <path d="M2 18 L6 18 L6 6 L10 6 L10 18 L22 18 L22 6 L26 6 L26 18 L38 18 L38 6 L42 6 L42 18 L54 18 L54 6 L58 6 L58 18 L70 18" />
      );
    case 'noise':
      // Dense noise scatter.
      return (
        <>
          {[
            [4, 14], [7, 8], [10, 17], [13, 6], [16, 15], [19, 10],
            [22, 18], [25, 7], [28, 14], [31, 5], [34, 19], [37, 11],
            [40, 8], [43, 16], [46, 6], [49, 14], [52, 18], [55, 9],
            [58, 15], [61, 7], [64, 17], [67, 11], [70, 14],
          ].map(([x, y], i) => (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={0.9}
              fill="currentColor"
              stroke="none"
            />
          ))}
        </>
      );
    case 'bell':
      // Impulse + harmonic tail.
      return (
        <>
          <path d="M4 20 L8 4 L12 20" />
          <path d="M14 12 Q 22 6, 30 14" opacity={0.7} />
          <path d="M30 14 Q 42 22, 52 14" opacity={0.5} />
          <path d="M52 14 Q 60 8, 70 14" opacity={0.35} />
        </>
      );
    case 'piano':
      // Stacked harmonics — keys glyph.
      return (
        <>
          <path d="M2 6 L18 6" />
          <path d="M2 12 L26 12" opacity={0.85} />
          <path d="M2 18 L34 18" opacity={0.7} />
          <path d="M40 4 L40 20" opacity={0.3} />
          <path d="M46 6 L62 6" opacity={0.7} />
          <path d="M46 12 L66 12" opacity={0.55} />
          <path d="M46 18 L70 18" opacity={0.4} />
        </>
      );
    case 'sectionSparse':
      // Intro / outro — low energy, gapped kick blocks with a
      // quiet sustained pad line.
      return (
        <>
          <rect x={4} y={13} width={4} height={7} fill="currentColor" stroke="none" opacity={0.9} />
          <rect x={22} y={13} width={4} height={7} fill="currentColor" stroke="none" opacity={0.9} />
          <rect x={40} y={13} width={4} height={7} fill="currentColor" stroke="none" opacity={0.9} />
          <rect x={58} y={13} width={4} height={7} fill="currentColor" stroke="none" opacity={0.9} />
          <path d="M2 6 L70 6" opacity={0.35} strokeDasharray="3 3" />
        </>
      );
    case 'sectionGroove':
      // Steady groove — four stacked tracks all present.
      return (
        <>
          <path d="M2 5 L70 5" opacity={0.9} />
          <path d="M2 10 L70 10" opacity={0.75} strokeDasharray="2 2" />
          <path d="M2 15 L70 15" opacity={0.6} strokeDasharray="1 2" />
          <path d="M2 19 L70 19" opacity={0.45} />
          <rect x={2} y={3} width={1.5} height={18} fill="currentColor" stroke="none" opacity={0.25} />
          <rect x={20} y={3} width={1.5} height={18} fill="currentColor" stroke="none" opacity={0.25} />
          <rect x={38} y={3} width={1.5} height={18} fill="currentColor" stroke="none" opacity={0.25} />
          <rect x={56} y={3} width={1.5} height={18} fill="currentColor" stroke="none" opacity={0.25} />
        </>
      );
    case 'sectionBuild':
      // Accelerating vertical lines — snare roll getting faster.
      return (
        <>
          {[4, 13, 22, 28, 34, 40, 45, 50, 54, 58, 61, 63, 65, 67, 69, 70].map((x, i) => (
            <path
              key={i}
              d={`M${x} ${20 - i * 0.6} L${x} 20`}
              opacity={0.35 + i * 0.04}
            />
          ))}
          <path d="M2 22 L70 22" opacity={0} />
        </>
      );
    case 'sectionDrop':
      // Drop — explosive full-mix: everything + crash.
      return (
        <>
          <path d="M36 2 L36 22" opacity={0.25} />
          <path d="M2 12 L14 4 L22 18 L30 6 L40 20 L48 4 L58 18 L66 6 L70 12" />
          <path d="M4 8 L8 14 L12 6 L16 16" opacity={0.5} />
          <path d="M60 14 L64 6 L68 16 L70 8" opacity={0.5} />
          <circle cx={36} cy={12} r={2} fill="currentColor" stroke="none" />
        </>
      );
    case 'sectionBreak':
      // Breakdown — drums drop out, leaving a sustained pad curve.
      return (
        <>
          <path d="M2 14 Q 18 6, 36 14 T 70 14" />
          <path d="M2 18 Q 20 22, 36 18 T 70 18" opacity={0.4} />
          <circle cx={14} cy={8} r={1} fill="currentColor" stroke="none" opacity={0.5} />
          <circle cx={58} cy={8} r={1} fill="currentColor" stroke="none" opacity={0.5} />
        </>
      );
    case 'sectionBridge':
      // Bridge — contrasting steps, new motif.
      return (
        <>
          <path d="M2 18 L14 18 L14 12 L26 12 L26 6 L38 6 L38 12 L50 12 L50 18 L62 18 L62 14 L70 14" />
          <path d="M2 21 L70 21" opacity={0.25} strokeDasharray="2 2" />
        </>
      );
  }
}
